/**
 * A miniature of an appearance, for the library cards and the template gallery.
 *
 * Deliberately **not** `ComposerPreview`. That component mounts the real
 * storefront in an iframe and clones every ERP stylesheet into it — correct for
 * one full-size canvas, far too heavy for a grid where every card would pay it.
 *
 * What it *does* reuse is the part that has to stay truthful: `themeVars` maps a
 * `StoreTheme` onto the same custom properties the storefront reads, so the
 * colours, radius and surfaces here are the store's real ones rather than a
 * designer's approximation. The shapes are a wireframe — that is honest about
 * being a summary, and it cannot drift into *claiming* to be the page the way a
 * hand-built look-alike would.
 */
import { useMemo } from "react";
import {
  themeVars,
  defaultStoreTheme,
  parseTheme,
  type HomeSection,
  type HomeSectionType,
} from "@flint/api-contract/store-home";

/** How each section type reads in miniature. */
const SHAPE: Record<HomeSectionType, "bar" | "hero" | "grid" | "band" | "text" | "rail"> = {
  announcement_bar: "bar",
  hero_slider: "hero",
  category_grid: "grid",
  product_grid: "grid",
  collection_rail: "rail",
  promo_banner: "band",
  stats_strip: "bar",
  about_block: "text",
  rich_text: "text",
  image_text: "band",
  testimonials: "text",
  faq: "text",
  logo_wall: "rail",
  gallery: "grid",
  featured_product: "band",
  contact: "text",
  newsletter: "bar",
  lead_capture: "bar",
};

export interface AppearanceThumbnailProps {
  /** Raw sections off the wire — unparsed, so unknown types degrade quietly. */
  sections: unknown[];
  /** Raw theme off the wire; `parseTheme` defaults anything unreadable. */
  theme: unknown;
  className?: string;
}

export function AppearanceThumbnail({ sections, theme, className }: AppearanceThumbnailProps) {
  const parsed = useMemo(() => parseTheme(theme), [theme]);
  const vars = useMemo(() => themeVars(parsed ?? defaultStoreTheme), [parsed]);

  // Only the enabled ones, and only the first handful — past about six rows the
  // miniature stops communicating anything.
  const rows = useMemo(() => {
    const list = Array.isArray(sections) ? sections : [];
    return list
      .filter((s): s is HomeSection => {
        const candidate = s as { enabled?: unknown; type?: unknown } | null;
        return Boolean(candidate?.enabled) && typeof candidate?.type === "string";
      })
      .slice(0, 6);
  }, [sections]);

  return (
    <div
      aria-hidden
      className={`flex flex-col gap-1 overflow-hidden p-2 ${className ?? ""}`}
      style={{
        background: parsed.bg,
        borderRadius: `${Math.min(parsed.radius, 12)}px`,
        // The theme's own tokens, so `var(--accent)` below is the store's accent.
        ...(vars as Record<string, string>),
      }}
    >
      {/* Header bar — every storefront has one, so the miniature does too. */}
      <div className="flex items-center gap-1">
        <div className="h-1.5 w-6 rounded-full" style={{ background: parsed.ink, opacity: 0.75 }} />
        <div className="ml-auto flex gap-0.5">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-1 w-2 rounded-full" style={{ background: parsed.muted, opacity: 0.5 }} />
          ))}
        </div>
      </div>

      {rows.length === 0 ? (
        <div
          className="flex flex-1 items-center justify-center rounded text-[9px]"
          style={{ background: parsed.surface, color: parsed.muted }}
        >
          Nothing shown yet
        </div>
      ) : (
        rows.map((section, i) => (
          <Row key={section.id ?? i} shape={SHAPE[section.type] ?? "bar"} theme={parsed} />
        ))
      )}
    </div>
  );
}

type ParsedTheme = ReturnType<typeof parseTheme>;

function Row({ shape, theme }: { shape: (typeof SHAPE)[HomeSectionType]; theme: ParsedTheme }) {
  const radius = `${Math.min(theme.radius, 6)}px`;

  if (shape === "hero") {
    return (
      <div
        className="flex shrink-0 flex-col justify-center gap-1 px-2"
        style={{ height: 30, background: theme.accent, borderRadius: radius }}
      >
        <div className="h-1.5 w-2/3 rounded-full" style={{ background: theme.accentFg, opacity: 0.9 }} />
        <div className="h-1 w-1/3 rounded-full" style={{ background: theme.accentFg, opacity: 0.6 }} />
      </div>
    );
  }

  if (shape === "grid") {
    return (
      <div className="grid shrink-0 grid-cols-4 gap-1">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} style={{ height: 12, background: theme.surface, borderRadius: radius, border: `1px solid ${theme.line}` }} />
        ))}
      </div>
    );
  }

  if (shape === "rail") {
    return (
      <div className="flex shrink-0 gap-1">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex-1" style={{ height: 10, background: theme.surface, borderRadius: radius, border: `1px solid ${theme.line}` }} />
        ))}
      </div>
    );
  }

  if (shape === "band") {
    return (
      <div
        className="flex shrink-0 items-center gap-1 p-1"
        style={{ height: 16, background: theme.surface, borderRadius: radius, border: `1px solid ${theme.line}` }}
      >
        <div className="h-full w-1/3 rounded-sm" style={{ background: theme.line }} />
        <div className="h-1 flex-1 rounded-full" style={{ background: theme.muted, opacity: 0.45 }} />
      </div>
    );
  }

  if (shape === "text") {
    return (
      <div className="flex shrink-0 flex-col gap-0.5">
        <div className="h-1 w-1/2 rounded-full" style={{ background: theme.ink, opacity: 0.55 }} />
        <div className="h-1 w-full rounded-full" style={{ background: theme.muted, opacity: 0.35 }} />
        <div className="h-1 w-4/5 rounded-full" style={{ background: theme.muted, opacity: 0.35 }} />
      </div>
    );
  }

  return (
    <div
      className="shrink-0 rounded-full"
      style={{ height: 5, background: theme.muted, opacity: 0.35, borderRadius: radius }}
    />
  );
}
